'use client';
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { MinusIcon, PlusIcon } from 'lucide-react';

import clsx from 'clsx';

// Las preguntas viven en src/data/faqs.js: son las mismas que emite el schema de
// SEO en Home.jsx. Este componente tenía su propia lista de 10, distinta de la
// del schema, así que el usuario leía una cosa y Google indexaba otra.
import { faqs as faqItems, categoriasFaq as categories } from '../../data/faqs';

export default function Faq2() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [expandedId, setExpandedId] = useState(null);

  const filteredFaqs =
    activeCategory === 'all'
      ? faqItems
      : faqItems.filter((item) => item.category === activeCategory);

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  // Las esquinas redondeadas son las mismas que cierran la franja gris de
  // arriba (Home.jsx): esta es la otra banda con fondo propio de la página, y
  // dejándola cuadrada quedaba una transición en seco a media pantalla de la
  // otra, que ya no la tiene.
  return (
    <section className="rounded-3xl bg-white/90 py-16 shadow-[0_0_30px_-12px_rgba(0,0,0,0.13)] backdrop-blur-md sm:rounded-[2.5rem]">
      <div className="container mx-auto max-w-6xl px-4 md:px-6">
        <div className="mb-12 flex flex-col items-center">
          <Badge
            variant="outline"
            className="border-primary mb-4 px-3 py-1 text-xs font-medium tracking-wider uppercase text-primary bg-primary/5">
            FAQ
          </Badge>

          <h2
            className="text-gray-900 mb-6 text-center text-4xl font-bold tracking-tight md:text-5xl">
            Preguntas Frecuentes
          </h2>

          <p className="text-gray-600 max-w-2xl text-center text-lg">
            Las dudas más comunes sobre Aturno y sobre cómo te va a simplificar la gestión de tu negocio.
          </p>
        </div>

        {/* Category Tabs */}
        <div className="mb-10 flex flex-wrap justify-center gap-2">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={clsx(
                'rounded-full px-4 py-2 text-sm font-medium transition-all duration-300',
                activeCategory === category.id
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'bg-white/80 text-gray-700 hover:bg-white border border-gray-200 hover:border-gray-300'
              )}>
              {category.label}
            </button>
          ))}
        </div>

        {/* FAQ Grid */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {/* `initial={false}`: al cargar la página las tarjetas ya están
              puestas. Sin esto entraban escalonadas de a 50ms, que es la misma
              entrada de a un bloque por vez que se sacó del resto de la landing
              (components/business/Aparecer.jsx). La animación sigue viva para
              cuando se cambia de categoría, que ahí sí es respuesta a un click
              y se entiende que las tarjetas se renueven. */}
          <AnimatePresence initial={false}>
            {filteredFaqs.map((faq, index) => (
              <motion.div
                key={faq.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className={clsx(
                  'border border-gray-200 h-fit overflow-hidden rounded-xl',
                  expandedId === faq.id
                    ? 'shadow-xl bg-white/95'
                    : 'bg-white/90 hover:shadow-lg'
                )}
                style={{ minHeight: '88px' }}>
                <button
                  onClick={() => toggleExpand(faq.id)}
                  className="flex w-full items-center justify-between p-6 text-left hover:bg-gray-50/50 transition-colors duration-200">
                  <h3 className="text-gray-900 text-lg font-medium">
                    {faq.question}
                  </h3>
                  <div className="ml-4 flex-shrink-0">
                    {expandedId === faq.id ? (
                      <MinusIcon className="text-primary h-5 w-5" />
                    ) : (
                      <PlusIcon className="text-primary h-5 w-5" />
                    )}
                  </div>
                </button>

                <AnimatePresence>
                  {expandedId === faq.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden">
                      <div className="border-t border-gray-200 px-6 pt-2 pb-6">
                        <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Contact CTA */}
        {/* Sin entrada, por lo mismo: este bloque salía medio segundo después
            que el resto y era el último paso del escáner. */}
        <div className="mt-16 text-center">
          <p className="text-gray-600 mb-4 text-lg">
            ¿No encontrás lo que buscás?
          </p>
          {/* Antes esto era un <a href="/contacto">, una ruta que no existe: el
              catch-all /:slug la interpretaba como el slug de un negocio y
              mostraba una página rota. No hay página ni casilla de contacto en
              el sitio, así que mandamos a la demo, que sí existe y responde la
              mayoría de las dudas. */}
          <Button asChild variant="outline" size="lg">
            <Link to="/demo">Miralo funcionando</Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
